import { useMemo } from 'react'

import IconCalendar from '@/assets/icons/icon-calendar.svg'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { weekdays } from '@/modules/tasks/service/repeatable-task-service.ts'
import { classNames } from '@/utils/helpers.ts'

const suffixes = ['th', 'st', 'nd', 'rd']

export function DayOfMonth({ day }: { day: number }) {
  return useMemo(() => {
    const index = (day > 3 && day < 21) || day % 10 > 3 ? 0 : day % 10
    const suffix = suffixes[index]

    return `${day}${suffix}`
  }, [day])
}

interface DayOfMonthSelectorProps {
  value: number
  onChange: (value: number) => void
  dayCount?: number
}

export const DayOfMonthSelector = (props: DayOfMonthSelectorProps) => {
  const { value, onChange, dayCount } = props

  return (
    <Popover>
      <PopoverTrigger className="w-[160px]">
        <div className="col-span-12 sm:col-span-6 group relative text-sm flex items-center gap-2 bg-card border rounded-md px-3 py-2 w-full truncate focus-within:border-primary-500 dark:hover:bg-secondary-600">
          <IconCalendar className="flex-shrink-0 w-4 h-4 text-secondary-300" />
          {value ? (
            <span>
              Every{' '}
              <strong className="font-medium">
                <DayOfMonth day={value} />
              </strong>{' '}
              day
            </span>
          ) : (
            <span className="text-secondary-300">Select the day</span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-6 rounded-lg shadow-2xl z-[102] max-w-[300px] select-auto border"
      >
        <div className="grid grid-cols-7 gap-2">
          {weekdays.map((weekday) => (
            <div
              key={weekday}
              className="text-xs font-medium text-secondary-300 uppercase"
            >
              {weekday.substring(0, 3)}
            </div>
          ))}
          {Array.from({ length: dayCount ?? 31 }, (_, index) => index + 1).map(
            (day) => (
              <button
                key={day}
                className={classNames(
                  'text-sm font-medium bg-secondary-50 dark:bg-secondary-500/40 rounded-md cursor-pointer transition ease-out duration-300',
                  {
                    'bg-primary-500 dark:bg-primary-500 text-white':
                      day === value,
                    'hover:bg-secondary-100 dark:hover:bg-secondary-500/60':
                      day !== value,
                  },
                )}
                onClick={() => onChange(day)}
              >
                {day}
              </button>
            ),
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
